const crypto = require('crypto');

/**
 * Encrypts plaintext using AES-256-CBC in OpenSSL-compatible format.
 * @param plaintext - The string to encrypt.
 * @param passphrase - The passphrase to derive the encryption key and IV.
 * @returns A base64-encoded string containing the "Salted__" header, salt, and ciphertext.
 */
export function encryptAes256CbcOpenSsl(password: string, passphrase: string): string {
  
  // Generate a random 8-byte salt, as crypto-js does with OpenSSL format
  const salt: Buffer = crypto.randomBytes(8);
  const passwordBuffer: Buffer = Buffer.from(passphrase, "utf-8");

  // Derive key and IV using EVP_BytesToKey (mimicking crypto-js's MD5-based derivation)
  const { key, iv }: { key: Buffer; iv: Buffer } = evpBytesToKey(passwordBuffer, salt, 32, 16);

  // Create AES cipher in CBC mode
  // @ts-expect-error - This is a valid use of Buffer.concat
  const cipher: crypto.Cipher = crypto.createCipheriv("aes-256-cbc", key, iv);

  // Encrypt the plaintext
  let ciphertext: Buffer = cipher.update(password, "utf-8");
  ciphertext = Buffer.concat([ciphertext, cipher.final()]);

  // Prepend the "Salted__" header and the salt to the ciphertext
  const saltedHeader: Buffer = Buffer.from("Salted__");
  const finalOutputBuffer: Buffer = Buffer.concat([saltedHeader, salt, ciphertext]);

  // Return the final output as a Base64 string
  return finalOutputBuffer.toString("base64");
}



/**
 * Derives a key and IV from a password and salt using an OpenSSL-compatible EVP_BytesToKey method with MD5.
 * @param passwordBuffer - The password as a Buffer.
 * @param salt - The salt as a Buffer (should be 8 bytes).
 * @param keyLength - Desired key length in bytes.
 * @param ivLength - Desired IV length in bytes.
 * @returns An object containing the derived `key` and `iv`, both as Buffers.
 */

function evpBytesToKey(
  passwordBuffer: Buffer,
  salt: Buffer,
  keyLength: number,
  ivLength: number
): { key: Buffer; iv: Buffer } {
  const derivedBytesNeeded = keyLength + ivLength;
  const derivedKeyAndIv = Buffer.alloc(derivedBytesNeeded);
  let previousHash = Buffer.alloc(0);
  let currentByteIndex = 0;

  // Continuously hash (previous hash + password + salt) until enough bytes are derived
  while (currentByteIndex < derivedBytesNeeded) {
    const hashInput = Buffer.concat([previousHash, passwordBuffer, salt]);
    const currentHash = crypto.createHash("md5").update(hashInput).digest();
    currentHash.copy(derivedKeyAndIv, currentByteIndex);
    currentByteIndex += currentHash.length;
    previousHash = currentHash;
  }

  return {
    key: derivedKeyAndIv.slice(0, keyLength),
    iv: derivedKeyAndIv.slice(keyLength, keyLength + ivLength),
  };
}
